import { useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { FileDown, Plus, FileText, Calendar as CalendarIcon, Users, ClipboardList } from "lucide-react";
import { cn } from "@/lib/utils";

const AVAILABLE_TABLES = [
  { value: 'claims', label: 'Claims' },
  { value: 'properties', label: 'Properties' },
  { value: 'placements', label: 'Placements' },
  { value: 'profiles', label: 'Users' },
  { value: 'user_roles', label: 'User Roles' },
  { value: 'claim_status_history', label: 'Claim Status History' },
  { value: 'notifications', label: 'Notifications' },
];

const TABLE_COLUMNS: Record<string, string[]> = {
  claims: ['claim_number', 'policyholder_name', 'status', 'claim_type', 'loss_date', 'created_at'],
  properties: ['name', 'address', 'city', 'state', 'bedrooms', 'bathrooms', 'rent_amount', 'status'],
  placements: ['client_id', 'landlord_id', 'property_id', 'start_date', 'end_date', 'status'],
  profiles: ['email', 'created_at'],
  user_roles: ['user_id', 'role', 'created_at'],
  claim_status_history: ['claim_id', 'old_status', 'new_status', 'created_at'],
  notifications: ['title', 'type', 'read', 'created_at'],
};

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  tables: string[];
  columns: Record<string, string[]>;
}

const REPORT_TEMPLATES: ReportTemplate[] = [
  {
    id: 'monthly-placements',
    name: 'Monthly Placements Summary',
    description: 'Overview of all placements with status, dates, and property details',
    icon: <CalendarIcon className="w-5 h-5" />,
    tables: ['placements', 'properties'],
    columns: {
      placements: ['client_id', 'landlord_id', 'property_id', 'start_date', 'end_date', 'status'],
      properties: ['name', 'address', 'city', 'state', 'bedrooms', 'rent_amount', 'status'],
    },
  },
  {
    id: 'user-activity',
    name: 'User Activity Report',
    description: 'User accounts, roles, and notification activity',
    icon: <Users className="w-5 h-5" />,
    tables: ['profiles', 'user_roles', 'notifications'],
    columns: {
      profiles: ['email', 'created_at'],
      user_roles: ['user_id', 'role', 'created_at'],
      notifications: ['title', 'type', 'read', 'created_at'],
    },
  },
  {
    id: 'claims-overview',
    name: 'Claims Overview',
    description: 'Complete claims data with status history and policyholder information',
    icon: <ClipboardList className="w-5 h-5" />,
    tables: ['claims', 'claim_status_history'],
    columns: {
      claims: ['claim_number', 'policyholder_name', 'status', 'claim_type', 'loss_date', 'created_at'],
      claim_status_history: ['claim_id', 'old_status', 'new_status', 'created_at'],
    },
  },
  {
    id: 'property-inventory',
    name: 'Property Inventory',
    description: 'All properties with details, availability, and pricing',
    icon: <FileText className="w-5 h-5" />,
    tables: ['properties'],
    columns: {
      properties: ['name', 'address', 'city', 'state', 'bedrooms', 'bathrooms', 'rent_amount', 'status'],
    },
  },
];

interface ReportBuilderProps {
  onReportCreated?: () => void;
}

export const ReportBuilder = ({ onReportCreated }: ReportBuilderProps) => {
  const [reportName, setReportName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<Record<string, string[]>>({});
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'templates' | 'custom'>('templates');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [templateStartDate, setTemplateStartDate] = useState<Date | undefined>();
  const [templateEndDate, setTemplateEndDate] = useState<Date | undefined>();

  const getDateRange = (start?: Date, end?: Date) => {
    if (!start && !end) return undefined;
    return {
      startDate: start ? format(start, 'yyyy-MM-dd') : format(new Date(0), 'yyyy-MM-dd'),
      endDate: end ? format(end, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    };
  };

  const applyTemplate = (template: ReportTemplate) => {
    setReportName(template.name);
    setDescription(template.description);
    setSelectedTables(template.tables);
    setSelectedColumns(template.columns);
    setActiveTab('custom');
    toast({ title: `Template "${template.name}" applied` });
  };

  const toggleTable = (table: string) => {
    if (selectedTables.includes(table)) {
      setSelectedTables(prev => prev.filter(t => t !== table));
      setSelectedColumns(prev => {
        const updated = { ...prev };
        delete updated[table];
        return updated;
      });
    } else {
      setSelectedTables(prev => [...prev, table]);
      setSelectedColumns(prev => ({
        ...prev,
        [table]: TABLE_COLUMNS[table] || []
      }));
    }
  };

  const toggleColumn = (table: string, column: string) => {
    setSelectedColumns(prev => {
      const tableColumns = prev[table] || [];
      if (tableColumns.includes(column)) {
        return {
          ...prev,
          [table]: tableColumns.filter(c => c !== column)
        };
      } else {
        return {
          ...prev,
          [table]: [...tableColumns, column]
        };
      }
    });
  };

  const handleSaveReport = async () => {
    if (!reportName.trim()) {
      toast({ title: "Please enter a report name", variant: "destructive" });
      return;
    }

    if (selectedTables.length === 0) {
      toast({ title: "Please select at least one table", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.from('custom_reports').insert({
        user_id: user.id,
        name: reportName,
        description: description || null,
        report_type: 'custom',
        tables: selectedTables,
        columns: selectedColumns,
        is_public: isPublic,
      });

      if (error) throw error;

      toast({ title: "Report saved successfully" });
      
      // Reset form
      setReportName('');
      setDescription('');
      setSelectedTables([]);
      setSelectedColumns({});
      setIsPublic(false);
      
      onReportCreated?.();
    } catch (error) {
      console.error('Error saving report:', error);
      toast({ title: "Failed to save report", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    if (selectedTables.length === 0) {
      toast({ title: "Please select at least one table", variant: "destructive" });
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      toast({ title: `Generating ${format.toUpperCase()} export...` });

      const { data, error } = await supabase.functions.invoke('export-report', {
        body: {
          reportName: reportName || 'Custom Report',
          format,
          tables: selectedTables,
          columns: selectedColumns,
          dateRange: getDateRange(startDate, endDate),
        },
      });

      if (error) throw error;

      // Convert the response to blob
      const response = new Response(JSON.stringify(data));

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${Date.now()}.${format === 'excel' ? 'csv' : 'html'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({ title: `${format.toUpperCase()} exported successfully` });
    } catch (error) {
      console.error('Error exporting:', error);
      toast({ title: "Export failed", variant: "destructive" });
    }
  };

  const handleQuickExport = async (template: ReportTemplate, format: 'pdf' | 'excel') => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      toast({ title: `Generating ${template.name} as ${format.toUpperCase()}...` });

      const { data, error } = await supabase.functions.invoke('export-report', {
        body: {
          reportName: template.name,
          format,
          tables: template.tables,
          columns: template.columns,
          dateRange: getDateRange(templateStartDate, templateEndDate),
        },
      });

      if (error) throw error;

      const response = new Response(JSON.stringify(data));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.id}_${Date.now()}.${format === 'excel' ? 'csv' : 'html'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({ title: `${template.name} exported successfully` });
    } catch (error) {
      console.error('Error exporting:', error);
      toast({ title: "Export failed", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'templates'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Report Templates
        </button>
        <button
          onClick={() => setActiveTab('custom')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'custom'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Custom Report Builder
        </button>
      </div>

      {activeTab === 'templates' && (
        <div className="space-y-6">
          {/* Date Range for Templates */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Date Range Filter</CardTitle>
              <CardDescription className="text-sm">
                Optional: Filter template data by date range
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[200px] justify-start text-left font-normal",
                          !templateStartDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {templateStartDate ? format(templateStartDate, "PPP") : <span>Pick start date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={templateStartDate}
                        onSelect={setTemplateStartDate}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[200px] justify-start text-left font-normal",
                          !templateEndDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {templateEndDate ? format(templateEndDate, "PPP") : <span>Pick end date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={templateEndDate}
                        onSelect={setTemplateEndDate}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                {(templateStartDate || templateEndDate) && (
                  <div className="flex items-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTemplateStartDate(undefined);
                        setTemplateEndDate(undefined);
                      }}
                    >
                      Clear dates
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            {REPORT_TEMPLATES.map((template) => (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg text-primary">
                      {template.icon}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <CardDescription className="text-sm">
                        {template.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex flex-wrap gap-1 mb-4">
                    {template.tables.map((table) => (
                      <span
                        key={table}
                        className="text-xs bg-accent px-2 py-1 rounded"
                      >
                        {AVAILABLE_TABLES.find(t => t.value === table)?.label || table}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleQuickExport(template, 'excel')}
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      Excel
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleQuickExport(template, 'pdf')}
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => applyTemplate(template)}
                    >
                      Customize
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'custom' && (
        <Card>
          <CardHeader>
            <CardTitle>Create Custom Report</CardTitle>
            <CardDescription>
              Select tables and columns to include in your report
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="reportName">Report Name</Label>
              <Input
                id="reportName"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="Enter report name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this report shows"
                rows={3}
              />
            </div>

            <div className="space-y-4">
              <Label>Select Tables</Label>
              {AVAILABLE_TABLES.map((table) => (
                <div key={table.value} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={table.value}
                      checked={selectedTables.includes(table.value)}
                      onCheckedChange={() => toggleTable(table.value)}
                    />
                    <label
                      htmlFor={table.value}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {table.label}
                    </label>
                  </div>

                  {selectedTables.includes(table.value) && TABLE_COLUMNS[table.value] && (
                    <div className="ml-6 space-y-2 p-3 border rounded-lg bg-accent/5">
                      <Label className="text-xs text-muted-foreground">Columns</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {TABLE_COLUMNS[table.value].map((column) => (
                          <div key={column} className="flex items-center space-x-2">
                            <Checkbox
                              id={`${table.value}-${column}`}
                              checked={selectedColumns[table.value]?.includes(column)}
                              onCheckedChange={() => toggleColumn(table.value, column)}
                            />
                            <label
                              htmlFor={`${table.value}-${column}`}
                              className="text-xs leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {column}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Date Range Filter */}
            <div className="space-y-4">
              <Label>Date Range Filter (Optional)</Label>
              <div className="flex flex-wrap gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[200px] justify-start text-left font-normal",
                          !startDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "PPP") : <span>Pick start date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[200px] justify-start text-left font-normal",
                          !endDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "PPP") : <span>Pick end date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                {(startDate || endDate) && (
                  <div className="flex items-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setStartDate(undefined);
                        setEndDate(undefined);
                      }}
                    >
                      Clear dates
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isPublic"
                checked={isPublic}
                onCheckedChange={(checked) => setIsPublic(checked as boolean)}
              />
              <label
                htmlFor="isPublic"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Make this report public (visible to all admins)
              </label>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveReport} disabled={saving}>
                <Plus className="w-4 h-4 mr-2" />
                Save Report
              </Button>
              <Button onClick={() => handleExport('excel')} variant="outline">
                <FileDown className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
              <Button onClick={() => handleExport('pdf')} variant="outline">
                <FileDown className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};